import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
    FileText, User, Calendar, Trash2, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const AdminReportsPage = () => {
    const { api } = useAdmin();
    const [reports, setReports] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const limit = 15;

    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/admin/reports?skip=${page * limit}&limit=${limit}`);
            setReports(response.data.reports);
            setTotal(response.data.total);
        } catch (error) {
            toast.error('Failed to fetch reports');
        } finally {
            setLoading(false);
        }
    }, [api, page]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleDelete = async () => {
        if (!reportToDelete) return;
        
        try {
            await api.delete(`/admin/reports/${reportToDelete.id}`);
            toast.success('Report deleted successfully');
            setDeleteDialogOpen(false);
            setReportToDelete(null);
            fetchReports();
        } catch (error) {
            toast.error('Failed to delete report');
        }
    };

    const confirmDelete = (report) => {
        setReportToDelete(report);
        setDeleteDialogOpen(true);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'critical': return 'bg-red-500/20 text-red-400';
            case 'abnormal': return 'bg-amber-500/20 text-amber-400';
            default: return 'bg-green-500/20 text-green-400';
        }
    };

    const getOverallStatus = (labValues) => {
        if (!labValues || labValues.length === 0) return 'normal';
        if (labValues.some(v => v.status === 'critical')) return 'critical';
        if (labValues.some(v => v.status === 'abnormal')) return 'abnormal';
        return 'normal';
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6" data-testid="admin-reports-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Reports Overview</h1>
                    <p className="text-slate-400 mt-1">View and manage all uploaded lab reports</p>
                </div>
                <Badge variant="outline" className="border-slate-600 text-slate-300 w-fit">
                    {total} total reports
                </Badge>
            </div>

            {/* Reports Table */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="reports-table">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left p-4 text-slate-400 font-medium">Report</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">User</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Panel Type</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-slate-400">
                                            Loading reports...
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-slate-400">
                                            No reports found
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map((report) => (
                                        <tr key={report.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                                                        <FileText className="w-5 h-5 text-teal-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium truncate max-w-[200px]">
                                                            {report.filename}
                                                        </p>
                                                        <p className="text-xs text-slate-500">ID: {report.id.slice(0, 8)}...</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {report.user ? (
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-slate-500" />
                                                        <div>
                                                            <p className="text-slate-300 text-sm">{report.user.name}</p>
                                                            <p className="text-xs text-slate-500">{report.user.email}</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-500">Unknown</span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <Badge variant="outline" className="border-slate-600 text-slate-300">
                                                    {report.panel_type || 'General'}
                                                </Badge>
                                            </td>
                                            <td className="p-4">
                                                <Badge className={`border-0 ${getStatusColor(getOverallStatus(report.lab_values))}`}>
                                                    {getOverallStatus(report.lab_values)}
                                                </Badge>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(report.upload_date)}
                                                </div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-400"
                                                        onClick={() => confirmDelete(report)}
                                                        data-testid={`delete-report-${report.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t border-slate-700">
                            <p className="text-sm text-slate-400">
                                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= totalPages - 1}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="bg-slate-800 border-slate-700">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Report</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            Are you sure you want to delete &quot;{reportToDelete?.filename}&quot;? This will permanently remove the report and all associated data.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminReportsPage;
